import cx from 'classnames';
import {xor, map, filter} from 'lodash';
import moment from 'moment';
import {useState, useContext, useMemo, useCallback, Fragment, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {WritingOptions} from 'xlsx';

import {sessionHistoryApi} from 'api';
import {
    DataWrapper,
    Button,
    Select,
    Card,
    notifier
} from 'components';
import {APP_NAME} from 'constants/app';
import {UserContext} from 'context';
import {useTask} from 'hooks';
import {SessionHistoryFullStatistic, ActivityStatistic} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {settingsUtils} from 'utils';
import {lazyModules} from 'utils/lazy-modules';
import {formatSeconds, transformUtils} from 'utils/transform';

import {ActivityMetricItem} from '../ActivityMetricItem';
import {SessionsResultInformation} from '../SessionsResultInformation';
import {REQUEST_SESSION_HISTORY_DATE_FORMAT} from '../constants';
import {ActivityMetric} from '../enums';
import {COUNTABLE_ACTIVITY_METRICS_CONFIGS, DURATION_ACTIVITY_METRICS_CONFIGS, CLIENT_DEVICE_METRICS_CONFIGS} from '../metric-config';
import {ActivityFilters} from '../types';

import {ActivityMetricsChart, AggregationType} from './ActivityMetricsChart';
import {ActivityMetricsFunnelChartCard} from './ActivityMetricsFunnelChartCard';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.DASHBOARD;
const T_KEYS_STATISTICS_FAILED = TRANSLATION_KEYS.USER.ACTIVITY.ERROR.STATISTICS_FAILED;
const SESSION_RESULT_T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_RESULT_INFORMATION_DETAILS;

type Props = {
    filters: ActivityFilters | null;
    isLoading: boolean;
    downloadFileDisabled?: boolean;
    onToggleLoading: (value: boolean) => void;
};

export const DashboardDetailed = ({filters, isLoading, downloadFileDisabled, onToggleLoading}: Props) => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);
    const canSeeFilePipeline = useMemo(() => settingsUtils.canSeeFilePipeline(settings), [settings]);
    const [statistic, setStatistic] = useState<SessionHistoryFullStatistic>(null);
    const [aggregationType, setAggregationType] = useState(AggregationType.Daily);
    const [selectedMetrics, setSelectedMetrics] = useState([
        ActivityMetric.Total,
        ActivityMetric.AvgResponseTime,
        ActivityMetric.ClientDeviceBrowser,
        ActivityMetric.ClientDeviceAndroidSdk,
        ActivityMetric.ClientDeviceIosSDK,
        ActivityMetric.ClientDevicePhone,
        ActivityMetric.ClientDeviceSip,
    ]);

    const {dateFormat} = useMemo(() => settings.common, [settings.common]);
    const fileDateFormat = useMemo(() => transformUtils.getFileDateFormat(dateFormat), [dateFormat]);

    const {startTask} = useTask(sessionHistoryApi.getActivityStatistics, {
        onComplete: setStatistic,
        onToggleLoading,
        notification: {
            enabled: true,
            labels: {
                error: t(T_KEYS_STATISTICS_FAILED)
            }
        }
    });

    useEffect(() => {
        if (startTask && filters) {
            const _filters = {
                ...filters,
                startDate: filters.startDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
                endDate: filters.endDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
            };
            startTask(_filters);
        }
    }, [filters, startTask]);

    const reportColumns = useMemo(() => ({
        DATE: t(T_KEYS.REPORT.COLUMNS.DATE),
        UNIQUE_VISITORS: t(T_KEYS.REPORT.COLUMNS.UNIQUE_VISITORS),
        ANSWERED: t(T_KEYS.REPORT.COLUMNS.ANSWERED),
        MISSED: t(T_KEYS.REPORT.COLUMNS.MISSED),
        TOTAL: t(T_KEYS.REPORT.COLUMNS.TOTAL),
        SUCCESSFUL: t(T_KEYS.REPORT.COLUMNS.SUCCESSFUL),
        FAILED: t(T_KEYS.REPORT.COLUMNS.FAILED),
        AVG_RESPONSE_TIME: t(T_KEYS.REPORT.COLUMNS.AVG_RESPONSE_TIME),
        AVG_DURATION: t(T_KEYS.REPORT.COLUMNS.AVG_DURATION),
        ALL_FILES_PROCESSED: t(T_KEYS.REPORT.COLUMNS.ALL_FILES_PROCESSED),
        SOME_FILES_UNPROCESSED: t(T_KEYS.REPORT.COLUMNS.SOME_FILES_UNPROCESSED),
    }), [t]);

    const availableCountableMetrics = useMemo(() => map(
        filter(COUNTABLE_ACTIVITY_METRICS_CONFIGS, (config) => !config.isHidden?.({settings})),
        (config) => config.metric
    ), [settings]);

    const aggregationOptions = useMemo(() => [{
        id: AggregationType.Hourly,
        name: t(T_KEYS.AGGREGATION.OPTIONS.HOURLY)
    },
    {
        id: AggregationType.Daily,
        name: t(T_KEYS.AGGREGATION.OPTIONS.DAILY)
    },
    {
        id: AggregationType.Monthly,
        name: t(T_KEYS.AGGREGATION.OPTIONS.MONTHLY)
    }], [t]);

    const aggregationValue = useMemo(() =>
        aggregationOptions.find((option) => option.id === aggregationType), [aggregationOptions, aggregationType]);

    const toggleMetricSelection = useCallback((metric: ActivityMetric) => {
        setSelectedMetrics((prev) => xor(prev, [metric]));
    }, []);

    const downloadReport = useCallback(async () => {
        if (!filters) {
            return;
        }

        const xlsx = await lazyModules.xlsx();
        if (!xlsx) {
            return;
        }

        const {startDate, endDate} = filters;

        if (!moment.isMoment(startDate) || !moment.isMoment(endDate)) {
            notifier.error({title: t(T_KEYS.ERRORS.SELECT_DATES)});
            return;
        }

        // TODO: fix typings for range
        const days = Array.from((moment as any).range(startDate, endDate).by('day'));

        if (!Array.isArray(days)) {
            return;
        }

        const wb = xlsx.utils.book_new();
        const sheetName = t(T_KEYS.REPORT.SHEET_NAME);
        const fileExtension = 'xlsx';

        const from = moment(startDate).format(fileDateFormat);
        const to = moment(endDate).format(fileDateFormat);
        const fileName = `${t(T_KEYS.REPORT.FILE_NAME_PREFIX)} ${from}-${to}.${fileExtension}`;
        const writeOpts: WritingOptions = {bookType: fileExtension, bookSST: true, type: 'binary', sheet: sheetName};

        const json = days.map((day) => {
            const dayStatistic: ActivityStatistic = statistic.days[moment(day).format('DD-MM-YYYY')] || {} as ActivityStatistic;
            return {
                [reportColumns.DATE]: moment(day).format(dateFormat),
                [reportColumns.UNIQUE_VISITORS]: dayStatistic.uniqueClients || 0,
                [reportColumns.ANSWERED]: dayStatistic.answered || 0,
                [reportColumns.MISSED]: dayStatistic.missed || 0,
                [reportColumns.TOTAL]: dayStatistic.total || 0,
                [reportColumns.SUCCESSFUL]: dayStatistic.successful || 0,
                [reportColumns.FAILED]: dayStatistic.failed || 0,
                [reportColumns.AVG_RESPONSE_TIME]: dayStatistic.avgResponseTime ? formatSeconds(dayStatistic.avgResponseTime) : 0,
                [reportColumns.AVG_DURATION]: dayStatistic.avgDuration ? formatSeconds(dayStatistic.avgDuration) : 0,
                ...(canSeeFilePipeline && {
                    [reportColumns.ALL_FILES_PROCESSED]: dayStatistic.allFilesProcessed || 0,
                    [reportColumns.SOME_FILES_UNPROCESSED]: dayStatistic.someFilesUnprocessed || 0,
                })
            };
        });

        const header = [
            reportColumns.DATE,
            reportColumns.UNIQUE_VISITORS,
            reportColumns.ANSWERED,
            reportColumns.MISSED,
            reportColumns.TOTAL,
            reportColumns.SUCCESSFUL,
            reportColumns.FAILED,
            ...(canSeeFilePipeline ?
                [reportColumns.ALL_FILES_PROCESSED, reportColumns.SOME_FILES_UNPROCESSED] : [])
        ];
        const ws = xlsx.utils.json_to_sheet(json, {header});

        wb.SheetNames.push(sheetName);
        wb.Sheets[sheetName] = ws;
        wb.Props = {Author: APP_NAME};

        xlsx.writeFile(wb, fileName, writeOpts);
    }, [t, filters, fileDateFormat, dateFormat, reportColumns, canSeeFilePipeline, statistic]);

    if (!filters) {
        return null;
    }

    return (
        <div className='tw-relative'>
            <div className='tw-my-4 tw-flex tw-items-end'>
                <Select
                    className='tw-w-40 tw-mr-4'
                    placeholder={t(T_KEYS.AGGREGATION.TITLE)}
                    options={aggregationOptions}
                    value={aggregationValue}
                    onChange={(value) => setAggregationType(value.id)}
                />
                <Button loading={isLoading} disabled={downloadFileDisabled} icon='arrow-down' onClick={downloadReport}>
                    {t(T_KEYS.BUTTONS.DOWNLOAD)}
                </Button>
            </div>
            <DataWrapper isLoading={isLoading} hideContentOnLoading={!statistic?.total?.total} noData={!statistic?.total?.total}>
                <div className={cx('tw-my-4 tw-grid tw-grid-cols-2 tw-gap-5',
                    availableCountableMetrics?.length === 6 ? 'md:tw-grid-cols-3 xl:tw-grid-cols-6' :
                        availableCountableMetrics?.length === 8 ? 'md:tw-grid-cols-4' : 'md:tw-grid-cols-4 xl:tw-grid-cols-5'
                )}
                >
                    {map(availableCountableMetrics, (metric) => (
                        <ActivityMetricItem
                            key={metric}
                            viewType='card'
                            metric={metric}
                            statistic={statistic?.total}
                            selected={selectedMetrics.includes(metric)}
                            onClick={() => toggleMetricSelection(metric)}
                        />
                    ))}
                </div>
                <div className='tw-my-4 tw-py-2 tw-bg-light'>
                    <div className='tw-h-80'>
                        <ActivityMetricsChart
                            startDate={filters.startDate}
                            endDate={filters.endDate}
                            statistic={statistic}
                            aggregationType={aggregationType}
                            metrics={selectedMetrics}
                            configs={COUNTABLE_ACTIVITY_METRICS_CONFIGS}
                        />
                    </div>
                </div>

                <div className='tw-grid tw-gap-2 tw-grid-cols-2 md:tw-grid-cols-4 xl:tw-grid-cols-5'>
                    {map(CLIENT_DEVICE_METRICS_CONFIGS, ({metric}) => (
                        <ActivityMetricItem
                            key={metric}
                            viewType='card'
                            metric={metric}
                            statistic={statistic?.total}
                            selected={selectedMetrics.includes(metric)}
                            onClick={() => toggleMetricSelection(metric)}
                        />
                    ))}
                </div>
                <div className='tw-my-4 tw-py-2 tw-h-80 tw-bg-light'>
                    <ActivityMetricsChart
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        statistic={statistic}
                        aggregationType={aggregationType}
                        metrics={selectedMetrics}
                        configs={CLIENT_DEVICE_METRICS_CONFIGS}
                    />
                </div>
                {aggregationType !== AggregationType.Hourly &&
                    <Fragment>
                        <div className='tw-grid tw-gap-2 tw-grid-cols-2 md:tw-grid-cols-4 xl:tw-grid-cols-5'>
                            {map(DURATION_ACTIVITY_METRICS_CONFIGS, ({metric}) => (
                                <ActivityMetricItem
                                    key={metric}
                                    viewType='card'
                                    metric={metric}
                                    statistic={statistic?.total}
                                    selected={selectedMetrics.includes(metric)}
                                    onClick={() => toggleMetricSelection(metric)}
                                />
                            ))}
                        </div>
                        <div className='tw-my-4 tw-py-2 tw-h-80 tw-bg-light'>
                            <ActivityMetricsChart
                                duration
                                startDate={filters.startDate}
                                endDate={filters.endDate}
                                statistic={statistic}
                                metrics={selectedMetrics}
                                configs={DURATION_ACTIVITY_METRICS_CONFIGS}
                            />
                        </div>
                    </Fragment>}

                {statistic && !isLoading &&
                    <div className='tw-my-4 -xl:tw-flex -md:tw-flex-col xl:tw-grid xl:tw-grid-cols-3 tw-w-full tw-overflow-auto'>
                        <ActivityMetricsFunnelChartCard statistic={statistic} className='tw-min-w-80 tw-flex-grow -md:tw-my-4 md:tw-mr-4' />
                        <Card fullWidth={false} className='tw-min-w-80 tw-flex-grow -md:tw-my-4 md:tw-mr-4'>
                            <SessionsResultInformation
                                header={t(SESSION_RESULT_T_KEYS.UNSUCCESSFUL_DETAILS)}
                                sessionResultInformationDetails={statistic?.total?.unsuccessfulSessionResultDetails}
                            />
                        </Card>
                        <Card fullWidth={false} className='tw-min-w-80 tw-flex-grow -md:tw-my-4'>
                            <SessionsResultInformation
                                header={t(SESSION_RESULT_T_KEYS.SUCCESSFUL_DETAILS)}
                                sessionResultInformationDetails={statistic?.total?.successfulSessionResultDetails}
                            />
                        </Card>
                    </div>}
            </DataWrapper>
        </div>
    );
};
